import Phaser from 'phaser';
import { asyncTween, Direction, gridMove, State, StateMachine, wait, WaitState } from 'tower/util';
import BattleScene from 'tower/scenes/battle';
import * as C from 'tower/constants';
import { Hittable } from 'tower/types';
import Room from './room';
import Projectile from './enemy/projectile';

const HIT_DELAY = 600;
const MOVE_DISTANCE = 16;

interface FlameOptions {
  direction?: Direction;
  limitHits?: boolean;
  persist?: boolean;
}

export default class Flame extends Phaser.GameObjects.Sprite {
  declare scene: BattleScene;
  declare body: Phaser.Physics.Arcade.Body;
  colliders: Phaser.Physics.Arcade.Collider[] = [];

  miscType = 'flame';
  stateMachine: StateMachine<State>;

  constructor(
    scene: BattleScene,
    room: Room,
    x: number,
    y: number,
    public flameOptions: FlameOptions = {},
  ) {
    super(scene, x, y, 'items', 38);

    scene.add.existing(this);
    scene.physics.add.existing(this);

    this.body.setSize(14, 14);

    this.setDepth(C.DEPTHS.Projectiles).play('flame');

    const hitTimes = new Map<Hittable, number>();
    const handleHit = (_flame: any, gameObject: any) => {
      const hittable = gameObject as unknown as Hittable;

      if (flameOptions.limitHits) {
        const hitTime = scene.time.now;
        const lastHitTime = hitTimes.get(hittable);
        if (lastHitTime && hitTime - lastHitTime < HIT_DELAY) {
          return;
        }

        hitTimes.set(hittable, hitTime);
      }

      hittable.hit({
        weaponType: 'flame',
        weaponBody: this.body,
        damage: 1,
      });
    };
    this.colliders.push(scene.physics.add.overlap(this, room.enemies, handleHit));
    this.colliders.push(scene.physics.add.overlap(this, scene.player, handleHit));

    this.stateMachine = new StateMachine<State>('idle', {
      idle: new FlameState(scene, this),
      wait: new WaitState([160, 170], 'shoot'),
      shoot: new FlameShootingState(scene, this),
    });

    (async () => {
      if (flameOptions.direction) {
        const [nx, ny] = gridMove(this.x, this.y, flameOptions.direction, MOVE_DISTANCE);
        await asyncTween(scene, {
          targets: [this],
          x: nx,
          y: ny,
          duration: 500,
        });
        if (this.isDestroyed) {
          return;
        }
      }

      if (!flameOptions.persist) {
        // If the current room isn't lit, light it up
        if (room.isDark) {
          scene.freeze();
          await room.transitionDark(false);
          room.isDark = false;
          scene.unfreeze();
        }

        await wait(scene, 700);
        this.scene.events.emit('destroyflame', this);
        this.destroy();
      }
    })();
  }

  update() {
    this.stateMachine.update();
  }

  startBlasting() {
    if (this.stateMachine.state === 'idle') {
      this.stateMachine.transition('shoot');
    }
  }

  destroy(fromScene?: boolean) {
    super.destroy(fromScene);
    for (const collider of this.colliders) {
      collider.destroy();
    }
  }

  static preload(scene: Phaser.Scene) {
    scene.load.audio('flame', 'audio/flame.mp3');
  }

  static create(scene: BattleScene) {
    scene.anims.create({
      key: 'flame',
      frameRate: 10,
      frames: scene.anims.generateFrameNumbers('items', {
        frames: [38, 39],
      }),
      repeat: -1,
    });
  }
}

class FlameState extends State {
  constructor(
    protected scene: BattleScene,
    protected flame: Flame,
  ) {
    super();
  }
}

class FlameShootingState extends FlameState {
  async handleEntered() {
    const { scene, flame } = this;
    const fireball = new Projectile(scene, flame.x, flame.y, 'fireball', Direction.Up, { target: 'player' });
    fireball.body.setVelocity(0);
    await wait(scene, 200);
    scene.physics.moveToObject(fireball, scene.player, C.FIREBALL_SPEED);
    return this.transition('wait');
  }
}
